/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState } from 'react';
import { message, Modal, Checkbox, Tooltip } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';

import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { CreateTenderAnswer } from './CreateTenderAnswer';
import {
  AnswerFormValues,
  defaultAnswerValues,
} from '~components/atoms/Questions/AnswerQuickForm';
import { appLog } from '~src/utils/log';
import { queryCache, useMutation } from 'react-query';
import {
  addAnswerToQuestion,
  updateQuestion,
} from '~src/services/api/questions';
import { QueryKeys } from '~src/hooks/api-hooks/queryKeys';
import { DbQuestion } from '~src/types/db';
import { useProjectId } from '~src/hooks/useProjectId';
import { QuestionFormValues } from '~components/atoms/Questions/QuestionForm';
import { manageCurrentCorrectionLetterQuestions } from '~src/services/api/movements';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export type CreateAnswerModalProps = Pick<ModalProps, 'visible'> & {
  question: DbQuestion;
  onCancel?: () => void;
};

export const CreateAnswerModal: React.FC<CreateAnswerModalProps> = ({
  onCancel,
  question,
  ...modalProps
}) => {
  const intl = useIntl();

  const projectId = useProjectId();
  const tenderIdMatch = window?.location?.pathname?.match(/tenders\/(\d+)/);
  const tenderId = tenderIdMatch ? String(tenderIdMatch[1]) : '';

  const [isRevisionChecked, setIsRevisionChecked] = useState(false);
  const [addToLetter, setAddToLetter] = useState(true);

  const [answerFormValues, setAnswerFormValues] = useState<AnswerFormValues>(
    defaultAnswerValues
  );
  const [
    reviseQuestionFormValues,
    setReviewQuestionFormValues,
  ] = useState<QuestionFormValues>({
    title: question.question,
    description: ``,
    question: question.question,
  });

  const [addAnswerMutation, { isLoading }] = useMutation(addAnswerToQuestion, {
    onSuccess: () => {
      queryCache.invalidateQueries([QueryKeys.sharedTenderQuestion]);
      queryCache.invalidateQueries([QueryKeys.question]);
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'questions-answering-failed' }));
      appLog('addAnswerMutation -> item', item);
      appLog('addAnswerMutation -> err', error);
    },
  });

  const [
    updateQuestionMutation,
    { isLoading: isUpdateQuestionLoading },
  ] = useMutation(updateQuestion, {
    onSuccess: () => {
      queryCache.invalidateQueries([QueryKeys.sharedTenderQuestion]);
      queryCache.invalidateQueries([QueryKeys.question]);
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'questions-answering-failed' }));
      appLog('addAnswerMutation -> item', item);
      appLog('addAnswerMutation -> err', error);
    },
  });

  const [doManageQuestionsToLetter] = useMutation(
    manageCurrentCorrectionLetterQuestions
  );

  const resetForm = () => {
    setIsRevisionChecked(false);
    setAnswerFormValues(defaultAnswerValues);
    setAddToLetter(true);
  };

  const isValid = () => {
    if (!answerFormValues.answer) {
      return false;
    }

    if (isRevisionChecked && !reviseQuestionFormValues.question) {
      return false;
    }

    return true;
  };

  const handleAnswerFormChange = (
    key: keyof AnswerFormValues,
    newValue: any
  ) => {
    setAnswerFormValues((prevForm) => ({
      ...prevForm,
      [key]: newValue,
    }));
  };

  const handleQuestionRevisionFormChange = (
    key: keyof QuestionFormValues,
    newValue: any
  ) => {
    setReviewQuestionFormValues((prevForm) => ({
      ...prevForm,
      [key]: newValue,
    }));
  };

  const handleSubmit = async () => {
    if (!isValid()) return;

    if (isRevisionChecked)
      await updateQuestionMutation({
        projectId,
        tenderId,
        questionId: question.id,
        data: reviseQuestionFormValues,
      });

    await addAnswerMutation({
      data: { answer: answerFormValues.answer },
      questionId: question.id,
      tenderId: tenderId!,
      projectId,
    });

    if (addToLetter && projectId && tenderId) {
      doManageQuestionsToLetter({
        projectId,
        tenderId: Number(tenderId),
        action: 'add_question',
        body: {
          question_ids: [question.id],
        },
      }).catch((err) =>
        appLog('Failed to add question to correctional letter', err)
      );
    }

    resetForm();
    onCancel?.();
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({
            id: 'answer-question',
          })}
          icon={['far', 'comments']}
        />
      }
      okText={intl.formatMessage({ id: 'answer' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      centered
      okButtonProps={{
        disabled: !isValid(),
        loading: isLoading || isUpdateQuestionLoading,
      }}
      onOk={handleSubmit}
      onCancel={() => {
        resetForm();
        onCancel?.();
      }}
      destroyOnClose
      width={800}
      {...modalProps}>
      <CreateTenderAnswer
        question={question}
        onAnswerFormChange={handleAnswerFormChange}
        answerFormValues={answerFormValues}
        reviseQuestionFormValues={reviseQuestionFormValues}
        onQuestionRevisionFormChange={handleQuestionRevisionFormChange}
        isRevisionChecked={isRevisionChecked}
        onRevisionCheckedChange={setIsRevisionChecked}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '20px',
        }}>
        <Checkbox
          checked={addToLetter}
          onChange={(e) => setAddToLetter(e.target.checked)}>
          {intl.formatMessage({ id: 'qa-add-to-correctional-letter' })}
          <Tooltip
            title={intl.formatMessage({ id: 'qa-add-to-correctional-letter' })}>
            <span style={{ marginLeft: '8px', color: '#8c8c8c' }}>
              <FontAwesomeIcon icon={['far', 'info-circle']} />
            </span>
          </Tooltip>
        </Checkbox>
      </div>
    </Modal>
  );
};
